// src/views/admin/ADEtablissement.jsx
//
// PERMISSIONS (module "parametres") :
// - parametres.modifier_etablissement : bouton "Modifier" des informations de l'établissement
// - parametres.gerer_niveaux          : créer / modifier / supprimer un niveau
// - parametres.gerer_series           : créer / modifier / supprimer une série
// Sans permission, les sections restent consultables en lecture seule.

import { useState, useEffect } from "react";
import { useAdminService } from "../../services/adminService";
import { Loader } from "../../components/Loader";
import { useToastHook } from "../../utils/useToast";
import { usePermissions } from "../../context/PermissionsContext";
import { urlimage } from "../../utils/config";

const getImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  if (path.startsWith('/')) return `${urlimage}${path}`;
  return `${urlimage}/${path}`;
};

function Modal({ title, onClose, children, maxWidth = 560 }) {
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" style={{ maxWidth }}>
        <div className="modal-title">
          {title}
          <button className="ibtn" style={{ border: "none" }} onClick={onClose}>
            <i className="ti ti-x" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

const CYCLE_LABELS = {
  PRESCOLAIRE: "Préscolaire",
  PRIMAIRE: "Primaire",
  COLLEGE: "Collège",
  LYCEE: "Lycée",
};

const TYPE_ETABLISSEMENT_OPTIONS = [
  { value: "PUBLIC", label: "Public" },
  { value: "PRIVE", label: "Privé" },
  { value: "CONFESSIONNEL", label: "Confessionnel" },
];

const EMPTY_NIVEAU = { nom: "", cycle: "", ordre: 0 };
const EMPTY_FILIERE = { nom: "", description: "", actif: true };

/**
 * ADEtablissement
 *
 * Peut être utilisé de deux façons :
 *  1) En autonomie :  <ADEtablissement slug={slug} />
 *     -> affiche son propre tab-nav (Établissement / Niveaux / Filières)
 *     et gère lui-même l'onglet actif.
 *
 *  2) Piloté par un parent (ex: ADParametres) :
 *     <ADEtablissement slug={slug} section="niveaux" hideNav />
 *     -> le parent décide de la section affichée via `section`
 *        ("infos" | "niveaux" | "filieres") et on masque le tab-nav
 *        interne avec `hideNav` pour éviter des onglets imbriqués.
 */
export default function ADEtablissement({ slug, section, hideNav = false }) {
  const { canSee } = usePermissions();
  const peutModifierEtab = canSee("parametres.modifier_etablissement");
  const peutGererNiveaux = canSee("parametres.gerer_niveaux");
  const peutGererSeries = canSee("parametres.gerer_series");

  const [internalTab, setInternalTab] = useState("infos");
  // Si "section" est fourni par le parent, c'est lui qui pilote l'onglet actif.
  const activeTab = section || internalTab;
  const setActiveTab = section ? () => {} : setInternalTab;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // ── Établissement ──
  const [etablissement, setEtablissement] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [cyclesDisponibles, setCyclesDisponibles] = useState([]);
  const [formEtablissement, setFormEtablissement] = useState({
    nom: "",
    sigle: "",
    numero_ecole: "",
    email: "",
    telephone: "",
    adresse: "",
    site_web: "",
    pays: "BURKINA FASO",
    devise_pays: "La patrie ou la mort, nous vaincrons",
    slogan: "",
    devise_ecole: "",
    logo: null,
    cachet: null,
    logo_pays: null,
  });
  const [logoPreview, setLogoPreview] = useState(null);
  const [cachetPreview, setCachetPreview] = useState(null);
  const [logoPaysPreview, setLogoPaysPreview] = useState(null);

  // ── Niveaux ──
  const [niveaux, setNiveaux] = useState([]);
  const [filterCycle, setFilterCycle] = useState("");
  const [modalNiveau, setModalNiveau] = useState(null);
  const [selectedNiveau, setSelectedNiveau] = useState(null);
  const [formNiveau, setFormNiveau] = useState(EMPTY_NIVEAU);

  // ── Filières ──
  const [filieres, setFilieres] = useState([]);
  const [filterFiliereActif, setFilterFiliereActif] = useState("");
  const [modalFiliere, setModalFiliere] = useState(null);
  const [selectedFiliere, setSelectedFiliere] = useState(null);
  const [formFiliere, setFormFiliere] = useState(EMPTY_FILIERE);

  const toast = useToastHook();
  const {
    getEtablissementCourant,
    updateEtablissement,
    getNiveaux, createNiveau, updateNiveau, deleteNiveau,
    getFilieres, createFiliere, updateFiliere, deleteFiliere,
  } = useAdminService();

  useEffect(() => {
    if (slug) loadData();
  }, [slug]);

  useEffect(() => {
    if (slug && activeTab === "niveaux") loadNiveaux();
    if (slug && activeTab === "filieres") loadFilieres();
  }, [slug, activeTab, filterCycle, filterFiliereActif]);

  const loadData = async () => {
    setLoading(true);
    try {
      const etabData = await getEtablissementCourant(slug);
      setEtablissement(etabData);

      const cycles = etabData.cycles || [];
      setCyclesDisponibles(cycles);

      setFormEtablissement({
        nom: etabData.nom || "",
        sigle: etabData.sigle || "",
        numero_ecole: etabData.numero_ecole || "",
        email: etabData.email || "",
        telephone: etabData.telephone || "",
        adresse: etabData.adresse || "",
        site_web: etabData.site_web || "",
        pays: etabData.pays || "BURKINA FASO",
        devise_pays: etabData.devise_pays || "La patrie ou la mort, nous vaincrons",
        slogan: etabData.slogan || "",
        devise_ecole: etabData.devise_ecole || "",
        logo: null,
        cachet: null,
        logo_pays: null,
      });

      // ✅ FIX : préfixer les chemins reçus de l'API avec l'URL complète,
      // sinon le preview en mode édition affiche une image cassée
      // (le mode affichage passait déjà par getImageUrl, pas le preview).
      setLogoPreview(etabData.logo ? getImageUrl(etabData.logo) : null);
      setCachetPreview(etabData.cachet ? getImageUrl(etabData.cachet) : null);
      setLogoPaysPreview(etabData.logo_pays ? getImageUrl(etabData.logo_pays) : null);

      await loadNiveaux();
      await loadFilieres();
    } catch (error) {
      toast.error("Impossible de charger les données");
    } finally {
      setLoading(false);
    }
  };

  const loadNiveaux = async () => {
    try {
      const params = {};
      if (filterCycle) params.cycle = filterCycle;
      const data = await getNiveaux(slug, params);
      setNiveaux(data || []);
    } catch (error) {
      toast.error("Impossible de charger les niveaux");
    }
  };

  const loadFilieres = async () => {
    try {
      const params = {};
      if (filterFiliereActif) params.actif = filterFiliereActif;
      const data = await getFilieres(slug, params);
      setFilieres(data || []);
    } catch (error) {
      toast.error("Impossible de charger les Séries");
    }
  };

  // ─── ÉTABLISSEMENT ──────────────────────────────────────────────────────
  const handleEtablissementChange = (field, value) => {
    setFormEtablissement(prev => ({ ...prev, [field]: value }));
  };

  const handleFileChange = (field, file) => {
    setFormEtablissement(prev => ({ ...prev, [field]: file }));
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (field === "logo") setLogoPreview(reader.result);
        if (field === "cachet") setCachetPreview(reader.result);
        if (field === "logo_pays") setLogoPaysPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveEtablissement = async () => {
    if (!peutModifierEtab) return;
    if (!formEtablissement.nom.trim()) { toast.error("Le nom est requis"); return; }
    if (!formEtablissement.sigle.trim()) { toast.error("Le sigle est requis"); return; }

    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('nom', formEtablissement.nom.trim());
      formData.append('sigle', formEtablissement.sigle.trim().toUpperCase());
      formData.append('numero_ecole', formEtablissement.numero_ecole || "");
      formData.append('email', formEtablissement.email || "");
      formData.append('telephone', formEtablissement.telephone || "");
      formData.append('adresse', formEtablissement.adresse || "");
      formData.append('site_web', formEtablissement.site_web || "");
      formData.append('pays', formEtablissement.pays || "BURKINA FASO");
      formData.append('devise_pays', formEtablissement.devise_pays || "La patrie ou la mort, nous vaincrons");
      formData.append('slogan', formEtablissement.slogan || "");
      formData.append('devise_ecole', formEtablissement.devise_ecole || "");

      if (formEtablissement.logo instanceof File) {
        formData.append('logo', formEtablissement.logo);
      }
      if (formEtablissement.cachet instanceof File) {
        formData.append('cachet', formEtablissement.cachet);
      }
      if (formEtablissement.logo_pays instanceof File) {
        formData.append('logo_pays', formEtablissement.logo_pays);
      }

      const updated = await updateEtablissement(slug, formData);
      setEtablissement(updated);
      setEditMode(false);
      toast.success("Établissement mis à jour avec succès");
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    } finally {
      setSaving(false);
    }
  };

  // ─── NIVEAUX ──────────────────────────────────────────────────────────────
  const openCreateNiveau = () => {
    const defaultCycle = cyclesDisponibles.length > 0 ? cyclesDisponibles[0] : "";
    setFormNiveau({ ...EMPTY_NIVEAU, cycle: defaultCycle });
    setModalNiveau("create");
  };

  const openEditNiveau = (n) => {
    setSelectedNiveau(n);
    setFormNiveau({ nom: n.nom, cycle: n.cycle, ordre: n.ordre || 0 });
    setModalNiveau("edit");
  };

  const openDeleteNiveau = (n) => {
    setSelectedNiveau(n);
    setModalNiveau("delete");
  };

  const handleNiveauChange = (field, value) => {
    setFormNiveau(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveNiveau = async () => {
    if (!peutGererNiveaux) return;
    if (!formNiveau.nom.trim()) { toast.error("Le nom est requis"); return; }
    if (!formNiveau.cycle) { toast.error("Le cycle est requis"); return; }

    setSaving(true);
    try {
      const dataToSend = {
        nom: formNiveau.nom.trim(),
        cycle: formNiveau.cycle,
        ordre: formNiveau.ordre || 0,
      };
      if (modalNiveau === "create") {
        const created = await createNiveau(slug, dataToSend);
        setNiveaux(prev => [...prev, created]);
        toast.success(`Niveau "${created.nom}" créé avec succès`);
      } else {
        const updated = await updateNiveau(slug, selectedNiveau.id, dataToSend);
        setNiveaux(prev => prev.map(n => n.id === selectedNiveau.id ? updated : n));
        toast.success(`Niveau "${updated.nom}" modifié avec succès`);
      }
      setModalNiveau(null);
      await loadNiveaux();
    } catch (error) {
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteNiveau = async () => {
    if (!peutGererNiveaux) return;
    setSaving(true);
    try {
      await deleteNiveau(slug, selectedNiveau.id);
      setNiveaux(prev => prev.filter(n => n.id !== selectedNiveau.id));
      toast.success("Niveau supprimé");
      setModalNiveau(null);
    } catch (error) {
      toast.error(error.response?.data?.error || "Impossible de supprimer ce niveau");
    } finally {
      setSaving(false);
    }
  };

  const cycleLabel = (val) => CYCLE_LABELS[val] || val;

  // ─── FILIÈRES ─────────────────────────────────────────────────────────────
  const openCreateFiliere = () => {
    setFormFiliere({ ...EMPTY_FILIERE });
    setModalFiliere("create");
  };

  const openEditFiliere = (f) => {
    setSelectedFiliere(f);
    setFormFiliere({ nom: f.nom, description: f.description || "", actif: f.actif });
    setModalFiliere("edit");
  };

  const openDeleteFiliere = (f) => {
    setSelectedFiliere(f);
    setModalFiliere("delete");
  };

  const handleFiliereChange = (field, value) => {
    setFormFiliere(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveFiliere = async () => {
    if (!peutGererSeries) return;
    if (!formFiliere.nom.trim()) { toast.error("Le nom est requis"); return; }

    setSaving(true);
    try {
      const dataToSend = {
        nom: formFiliere.nom.trim(),
        description: formFiliere.description || "",
        actif: formFiliere.actif,
      };
      if (modalFiliere === "create") {
        const created = await createFiliere(slug, dataToSend);
        setFilieres(prev => [...prev, created]);
        toast.success(`Séries "${created.nom}" créée avec succès`);
      } else {
        const updated = await updateFiliere(slug, selectedFiliere.id, dataToSend);
        setFilieres(prev => prev.map(f => f.id === selectedFiliere.id ? updated : f));
        toast.success(`Séries "${updated.nom}" modifiée avec succès`);
      }
      setModalFiliere(null);
      await loadFilieres();
    } catch (error) {
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteFiliere = async () => {
    if (!peutGererSeries) return;
    setSaving(true);
    try {
      await deleteFiliere(slug, selectedFiliere.id);
      setFilieres(prev => prev.filter(f => f.id !== selectedFiliere.id));
      toast.success("Séries supprimée");
      setModalFiliere(null);
    } catch (error) {
      toast.error(error.response?.data?.error || "Impossible de supprimer cette Séries");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <div>
      {!hideNav && (
        <div className="tab-nav">
          <button className={`tab-btn ${activeTab === "infos" ? "active" : ""}`} onClick={() => setActiveTab("infos")}>
            <i className="ti ti-building" /> Établissement
          </button>
          <button className={`tab-btn ${activeTab === "niveaux" ? "active" : ""}`} onClick={() => setActiveTab("niveaux")}>
            <i className="ti ti-layers" /> Niveaux
          </button>
          <button className={`tab-btn ${activeTab === "filieres" ? "active" : ""}`} onClick={() => setActiveTab("filieres")}>
            <i className="ti ti-columns" /> Séries
          </button>
        </div>
      )}

      {/* ─── TAB ÉTABLISSEMENT ─── */}
      {activeTab === "infos" && etablissement && (
        <div className="card">
          <div className="ch">
            <div className="ct">Informations de l'établissement</div>
            {!editMode ? (
              /* parametres.modifier_etablissement */
              peutModifierEtab && (
                <button className="bprim" onClick={() => setEditMode(true)}>
                  <i className="ti ti-edit" /> Modifier
                </button>
              )
            ) : (
              <div style={{ display: "flex", gap: 7 }}>
                <button className="bsec" onClick={() => { setEditMode(false); loadData(); }} disabled={saving}>
                  Annuler
                </button>
                <button className="bprim" onClick={handleSaveEtablissement} disabled={saving}>
                  {saving ? "En cours..." : <><i className="ti ti-check" /> Enregistrer</>}
                </button>
              </div>
            )}
          </div>

          {!editMode ? (
            /* ─── MODE AFFICHAGE ─── */
            <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
              <div style={{ textAlign: "center", flexShrink: 0 }}>
                {etablissement.logo ? (
                  <img src={getImageUrl(etablissement.logo)} alt="Logo"
                    style={{ width: 150, height: 150, objectFit: "contain", borderRadius: 8, border: "1px solid var(--b)" }} />
                ) : (
                  <div style={{ width: 150, height: 150, background: "var(--s2)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid var(--b)", color: "var(--h)" }}>
                    <i className="ti ti-building" style={{ fontSize: 48 }} />
                  </div>
                )}
                <div style={{ fontSize: 12, color: "var(--m)", marginTop: 8 }}>Logo</div>
              </div>

              <div style={{ textAlign: "center", flexShrink: 0 }}>
                {etablissement.cachet ? (
                  <img src={getImageUrl(etablissement.cachet)} alt="Cachet"
                    style={{ width: 150, height: 150, objectFit: "contain", borderRadius: 8, border: "1px solid var(--b)" }} />
                ) : (
                  <div style={{ width: 150, height: 150, background: "var(--s2)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid var(--b)", color: "var(--h)" }}>
                    <i className="ti ti-stamp" style={{ fontSize: 48 }} />
                  </div>
                )}
                <div style={{ fontSize: 12, color: "var(--m)", marginTop: 8 }}>Cachet</div>
              </div>

              <div style={{ textAlign: "center", flexShrink: 0 }}>
                {etablissement.logo_pays ? (
                  <img src={getImageUrl(etablissement.logo_pays)} alt="Logo pays"
                    style={{ width: 150, height: 150, objectFit: "contain", borderRadius: 8, border: "1px solid var(--b)" }} />
                ) : (
                  <div style={{ width: 150, height: 150, background: "var(--s2)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid var(--b)", color: "var(--h)" }}>
                    <i className="ti ti-flag" style={{ fontSize: 48 }} />
                  </div>
                )}
                <div style={{ fontSize: 12, color: "var(--m)", marginTop: 8 }}>Logo pays</div>
              </div>

              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ fontSize: 22, fontWeight: 700, color: "var(--t)" }}>
                  {etablissement.nom || "—"}
                </div>
                <div style={{ fontSize: 14, color: "var(--m)", marginBottom: 12 }}>
                  <span className="pill info">{etablissement.sigle || "—"}</span>
                  <span className="pill neu" style={{ marginLeft: 6 }}>
                    {TYPE_ETABLISSEMENT_OPTIONS.find(t => t.value === etablissement.type_etablissement)?.label || etablissement.type_etablissement}
                  </span>
                  <span className={`pill ${etablissement.active ? "ok" : "neu"}`} style={{ marginLeft: 6 }}>
                    {etablissement.active ? "Actif" : "Inactif"}
                  </span>
                </div>

                {etablissement.slogan && (
                  <div style={{ fontSize: 14, fontStyle: "italic", color: "var(--m)", marginBottom: 8 }}>
                    « {etablissement.slogan} »
                  </div>
                )}

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, fontSize: 14 }}>
                  <div><span style={{ color: "var(--m)" }}>Numéro établissement :</span> {etablissement.numero_ecole || "—"}</div>
                  <div><span style={{ color: "var(--m)" }}>Email :</span> {etablissement.email || "—"}</div>
                  <div><span style={{ color: "var(--m)" }}>Téléphone :</span> {etablissement.telephone || "—"}</div>
                  <div><span style={{ color: "var(--m)" }}>Site web :</span> {etablissement.site_web || "—"}</div>
                  <div style={{ gridColumn: "span 2" }}><span style={{ color: "var(--m)" }}>Adresse :</span> {etablissement.adresse || "—"}</div>
                  <div><span style={{ color: "var(--m)" }}>Devise de l'école :</span> {etablissement.devise_ecole || "—"}</div>
                  <div><span style={{ color: "var(--m)" }}>Pays :</span> {etablissement.pays || "—"}</div>
                  <div style={{ gridColumn: "span 2" }}><span style={{ color: "var(--m)" }}>Devise nationale :</span> {etablissement.devise_pays || "—"}</div>
                </div>

                {etablissement.cycles && etablissement.cycles.length > 0 && (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "var(--t)", marginBottom: 4 }}>
                      Cycles disponibles :
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                      {etablissement.cycles.map(c => (
                        <span key={c} className="pill neu" style={{ fontSize: 12 }}>
                          {CYCLE_LABELS[c] || c}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* ─── MODE ÉDITION ─── */
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div className="fgrid">
                <div className="fg">
                  <label>Nom *</label>
                  <input value={formEtablissement.nom} onChange={e => handleEtablissementChange('nom', e.target.value)} />
                </div>
                <div className="fg">
                  <label>Sigle *</label>
                  <input value={formEtablissement.sigle} onChange={e => handleEtablissementChange('sigle', e.target.value.toUpperCase())} />
                </div>
              </div>

              <div className="fg">
                <label>Numéro d'établissement</label>
                <input value={formEtablissement.numero_ecole} onChange={e => handleEtablissementChange('numero_ecole', e.target.value)} />
              </div>

              <div className="fgrid">
                <div className="fg">
                  <label>Email</label>
                  <input type="email" value={formEtablissement.email} onChange={e => handleEtablissementChange('email', e.target.value)} />
                </div>
                <div className="fg">
                  <label>Téléphone</label>
                  <input value={formEtablissement.telephone} onChange={e => handleEtablissementChange('telephone', e.target.value)} />
                </div>
              </div>

              <div className="fg">
                <label>Site web</label>
                <input value={formEtablissement.site_web} onChange={e => handleEtablissementChange('site_web', e.target.value)} placeholder="https://..." />
              </div>

              <div className="fg">
                <label>Adresse</label>
                <textarea value={formEtablissement.adresse} onChange={e => handleEtablissementChange('adresse', e.target.value)} rows={2} />
              </div>

              <div className="fgrid">
                <div className="fg">
                  <label>Slogan de l'école</label>
                  <input value={formEtablissement.slogan} onChange={e => handleEtablissementChange('slogan', e.target.value)} />
                </div>
                <div className="fg">
                  <label>Devise de l'école</label>
                  <input value={formEtablissement.devise_ecole} onChange={e => handleEtablissementChange('devise_ecole', e.target.value)} />
                </div>
              </div>

              <div className="fgrid">
                <div className="fg">
                  <label>Pays</label>
                  <input value={formEtablissement.pays} onChange={e => handleEtablissementChange('pays', e.target.value)} />
                </div>
                <div className="fg">
                  <label>Devise nationale</label>
                  <input value={formEtablissement.devise_pays} onChange={e => handleEtablissementChange('devise_pays', e.target.value)} />
                </div>
              </div>

              {/* ✅ FIX : les previews utilisent maintenant directement logoPreview /
                  cachetPreview / logoPaysPreview, déjà préfixées correctement au
                  chargement (existant) ou en base64 (nouveau fichier choisi) —
                  plus besoin des blocs de repli redondants sur `etablissement.x` */}
              <div className="fgrid">
                <div className="fg">
                  <label>Logo</label>
                  <input type="file" accept="image/*" onChange={e => handleFileChange('logo', e.target.files?.[0])} />
                  {logoPreview && (
                    <img
                      src={logoPreview}
                      alt="Logo preview"
                      style={{ width: 80, height: 80, objectFit: "contain", marginTop: 6, borderRadius: 4, border: "1px solid var(--b)", background: "var(--s2)" }}
                    />
                  )}
                </div>
                <div className="fg">
                  <label>Cachet</label>
                  <input type="file" accept="image/*" onChange={e => handleFileChange('cachet', e.target.files?.[0])} />
                  {cachetPreview && (
                    <img
                      src={cachetPreview}
                      alt="Cachet preview"
                      style={{ width: 80, height: 80, objectFit: "contain", marginTop: 6, borderRadius: 4, border: "1px solid var(--b)", background: "var(--s2)" }}
                    />
                  )}
                </div>
                <div className="fg">
                  <label>Logo du pays</label>
                  <input type="file" accept="image/*" onChange={e => handleFileChange('logo_pays', e.target.files?.[0])} />
                  {logoPaysPreview && (
                    <img
                      src={logoPaysPreview}
                      alt="Logo pays preview"
                      style={{ width: 80, height: 80, objectFit: "contain", marginTop: 6, borderRadius: 4, border: "1px solid var(--b)", background: "var(--s2)" }}
                    />
                  )}
                </div>
              </div>

              <div style={{
                padding: "12px 16px",
                background: "var(--s2)",
                borderRadius: 6,
                border: "0.5px solid var(--b)",
                fontSize: 13,
                color: "var(--m)"
              }}>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
                  <div>
                    <strong>Type :</strong> {TYPE_ETABLISSEMENT_OPTIONS.find(t => t.value === etablissement.type_etablissement)?.label || etablissement.type_etablissement}
                  </div>
                  <div>
                    <strong>Statut :</strong> {etablissement.active ? "Actif" : "Inactif"}
                  </div>
                  <div>
                    <strong>Cycles :</strong> {(etablissement.cycles || []).map(c => CYCLE_LABELS[c] || c).join(", ") || "Aucun"}
                  </div>
                  <div style={{ fontSize: 12, color: "var(--h)" }}>
                    <i className="ti ti-info-circle" /> Ces informations sont gérées par le super administrateur
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─── TAB NIVEAUX — parametres.gerer_niveaux ─── */}
      {activeTab === "niveaux" && (
        <div>
          <div className="sb2">
            <select value={filterCycle} onChange={e => setFilterCycle(e.target.value)}>
              <option value="">Tous les cycles</option>
              {cyclesDisponibles.map(cycle => (
                <option key={cycle} value={cycle}>{CYCLE_LABELS[cycle] || cycle}</option>
              ))}
            </select>
            <button className="bsec" onClick={loadNiveaux}><i className="ti ti-refresh" />Actualiser</button>
            {peutGererNiveaux && (
              <button className="bprim" onClick={openCreateNiveau}>
                <i className="ti ti-plus" />Nouveau niveau
              </button>
            )}
          </div>

          <div className="card" style={{ padding: 0, overflow: "hidden" }}>
            <table className="tbl">
              <thead>
                <tr>
                  <th>Nom</th>
                  <th>Cycle</th>
                  <th>Ordre</th>
                  {peutGererNiveaux && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {niveaux.map(n => (
                  <tr key={n.id}>
                    <td style={{ fontWeight: 500 }}>{n.nom}</td>
                    <td><span className="pill info">{cycleLabel(n.cycle)}</span></td>
                    <td>{n.ordre || "—"}</td>
                    {peutGererNiveaux && (
                      <td>
                        <div className="act">
                          <button title="Modifier" onClick={() => openEditNiveau(n)}
                            style={{ background: "#ffc107", color: "black" }}
                            onMouseEnter={e => { e.currentTarget.style.opacity = "0.8"; }}
                            onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }}>
                            <i className="ti ti-edit" />
                          </button>
                          <button className="del" title="Supprimer" onClick={() => openDeleteNiveau(n)}>
                            <i className="ti ti-trash" />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
                {niveaux.length === 0 && (
                  <tr><td colSpan={peutGererNiveaux ? 4 : 3} style={{ textAlign: "center", color: "var(--h)", padding: "24px" }}>
                    Aucun niveau trouvé
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ─── TAB FILIÈRES — parametres.gerer_series ─── */}
      {activeTab === "filieres" && (
        <div>
          <div className="sb2">
            <select value={filterFiliereActif} onChange={e => setFilterFiliereActif(e.target.value)}>
              <option value="">Toutes</option>
              <option value="true">Actives</option>
              <option value="false">Inactives</option>
            </select>
            <button className="bsec" onClick={loadFilieres}><i className="ti ti-refresh" />Actualiser</button>
            {peutGererSeries && (
              <button className="bprim" onClick={openCreateFiliere}>
                <i className="ti ti-plus" />Nouvelle Séries
              </button>
            )}
          </div>

          <div className="card" style={{ padding: 0, overflow: "hidden" }}>
            <table className="tbl">
              <thead>
                <tr>
                  <th>Nom</th>
                  <th>Description</th>
                  <th>Statut</th>
                  {peutGererSeries && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {filieres.map(f => (
                  <tr key={f.id}>
                    <td style={{ fontWeight: 500 }}>{f.nom}</td>
                    <td style={{ color: "var(--m)", fontSize: 13 }}>{f.description || "—"}</td>
                    <td>
                      <span className={`pill ${f.actif ? "ok" : "neu"}`}>
                        {f.actif ? "Active" : "Inactive"}
                      </span>
                    </td>
                    {peutGererSeries && (
                      <td>
                        <div className="act">
                          <button title="Modifier" onClick={() => openEditFiliere(f)}
                            style={{ background: "#ffc107", color: "black" }}
                            onMouseEnter={e => { e.currentTarget.style.opacity = "0.8"; }}
                            onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }}>
                            <i className="ti ti-edit" />
                          </button>
                          <button className="del" title="Supprimer" onClick={() => openDeleteFiliere(f)}>
                            <i className="ti ti-trash" />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
                {filieres.length === 0 && (
                  <tr><td colSpan={peutGererSeries ? 4 : 3} style={{ textAlign: "center", color: "var(--h)", padding: "24px" }}>
                    Aucune Séries trouvée
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ─── MODAL NIVEAU ─── */}
      {peutGererNiveaux && (modalNiveau === "create" || modalNiveau === "edit") && (
        <Modal title={modalNiveau === "create" ? "Nouveau niveau" : "Modifier le niveau"} onClose={() => setModalNiveau(null)}>
          <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
            <div className="fg">
              <label>Nom *</label>
              <input value={formNiveau.nom} onChange={e => handleNiveauChange('nom', e.target.value)} />
            </div>
            <div className="fg">
              <label>Cycle *</label>
              <select value={formNiveau.cycle} onChange={e => handleNiveauChange('cycle', e.target.value)}>
                <option value="">-- Sélectionner un cycle --</option>
                {cyclesDisponibles.map(cycle => (
                  <option key={cycle} value={cycle}>{CYCLE_LABELS[cycle] || cycle}</option>
                ))}
              </select>
            </div>
            <div className="fg">
              <label>Ordre (position)</label>
              <input type="number" value={formNiveau.ordre || 0} onChange={e => handleNiveauChange('ordre', parseInt(e.target.value) || 0)} />
            </div>
          </div>
          <div className="factions">
            <button className="bsec" onClick={() => setModalNiveau(null)} disabled={saving}>Annuler</button>
            <button className="bprim" onClick={handleSaveNiveau} disabled={saving}>
              {saving ? "En cours..." : <><i className="ti ti-check" /> {modalNiveau === "create" ? "Créer" : "Enregistrer"}</>}
            </button>
          </div>
        </Modal>
      )}

      {peutGererNiveaux && modalNiveau === "delete" && selectedNiveau && (
        <Modal title="Confirmer la suppression" onClose={() => setModalNiveau(null)}>
          <div style={{ fontSize: 14, color: "var(--m)" }}>
            Supprimer le niveau <strong>{selectedNiveau.nom}</strong> ?
            {selectedNiveau.classes_count > 0 && (
              <div style={{ color: "var(--rd)", marginTop: 8 }}>
                ⚠️ Ce niveau contient {selectedNiveau.classes_count} classe(s). La suppression est bloquée.
              </div>
            )}
          </div>
          <div className="factions">
            <button className="bsec" onClick={() => setModalNiveau(null)} disabled={saving}>Annuler</button>
            <button className="bprim rd" onClick={handleDeleteNiveau} disabled={saving || selectedNiveau.classes_count > 0}>
              {saving ? "..." : <><i className="ti ti-trash" />Supprimer</>}
            </button>
          </div>
        </Modal>
      )}

      {/* ─── MODAL FILIÈRE ─── */}
      {peutGererSeries && (modalFiliere === "create" || modalFiliere === "edit") && (
        <Modal title={modalFiliere === "create" ? "Nouvelle Séries" : "Modifier la Séries"} onClose={() => setModalFiliere(null)}>
          <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
            <div className="fg">
              <label>Nom *</label>
              <input value={formFiliere.nom} onChange={e => handleFiliereChange('nom', e.target.value)} />
            </div>
            <div className="fg">
              <label>Description</label>
              <textarea value={formFiliere.description} onChange={e => handleFiliereChange('description', e.target.value)} rows={2} />
            </div>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 14 }}>
              <input type="checkbox" checked={formFiliere.actif} onChange={e => handleFiliereChange('actif', e.target.checked)} />
              Séries active
            </label>
          </div>
          <div className="factions">
            <button className="bsec" onClick={() => setModalFiliere(null)} disabled={saving}>Annuler</button>
            <button className="bprim" onClick={handleSaveFiliere} disabled={saving}>
              {saving ? "En cours..." : <><i className="ti ti-check" /> {modalFiliere === "create" ? "Créer" : "Enregistrer"}</>}
            </button>
          </div>
        </Modal>
      )}

      {peutGererSeries && modalFiliere === "delete" && selectedFiliere && (
        <Modal title="Confirmer la suppression" onClose={() => setModalFiliere(null)}>
          <div style={{ fontSize: 14, color: "var(--m)" }}>
            Supprimer la Séries <strong>{selectedFiliere.nom}</strong> ?
            {selectedFiliere.classes_count > 0 && (
              <div style={{ color: "var(--rd)", marginTop: 8 }}>
                ⚠️ Cette Séries contient {selectedFiliere.classes_count} classe(s). La suppression est bloquée.
              </div>
            )}
          </div>
          <div className="factions">
            <button className="bsec" onClick={() => setModalFiliere(null)} disabled={saving}>Annuler</button>
            <button className="bprim rd" onClick={handleDeleteFiliere} disabled={saving || selectedFiliere.classes_count > 0}>
              {saving ? "..." : <><i className="ti ti-trash" />Supprimer</>}
            </button>
          </div>
        </Modal>
      )}

      <style>{`
        .tab-nav { display: flex; gap: 4px; flex-wrap: wrap; background: var(--pr); border-radius: 10px; padding: 8px 12px; margin-bottom: 16px; box-shadow: 0 2px 8px rgba(0,0,0,0.1); }
        .tab-btn { display: flex; align-items: center; gap: 6px; padding: 10px 18px; border: none; border-radius: 6px; font-size: 13px; font-weight: 600; cursor: pointer; transition: all 0.3s ease; background: transparent; color: rgba(255,255,255,0.7); font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; }
        .tab-btn i { font-size: 18px; }
        .tab-btn:hover { background: rgba(255,255,255,0.15); color: white; }
        .tab-btn.active { background: white; color: var(--pr); box-shadow: 0 2px 8px rgba(0,0,0,0.15); }
        .tab-btn.active:hover { background: white; color: var(--pr); }
        .fgrid { display: grid; grid-template-columns: 1fr 1fr; gap: 9px; }
        .act button { width: 30px; height: 30px; border-radius: 4px; border: none; cursor: pointer; display: flex; align-items: center; justify-content: center; font-size: 14px; transition: all .2s; }
        .act button:hover { opacity: 0.8 !important; }
        .act button.del { background: var(--rd); color: white; }
        .act button.del:hover { background: var(--rd-dk); }
        @media (max-width: 768px) { .tab-btn { padding: 6px 10px; font-size: 11px; } .tab-btn i { font-size: 14px; } .fgrid { grid-template-columns: 1fr; } }
        @media (max-width: 576px) { .tab-btn span { display: none; } .tab-btn i { font-size: 18px; margin: 0; } }
      `}</style>
    </div>
  );
}